/* eslint-disable i18next/no-literal-string */
import {
  Box,
  Button,
  Chip,
  Icon,
  IconButton,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  OSSubscriptions,
  centsPerLearnerPerMeeting,
  credentialDisplaySort,
} from "@outschool/business-rules";
import { DegreeSubject } from "@outschool/gql-backend-generated";
import {
  LightListingCard_ListingFragment,
  ListingCard_ListingFragment,
} from "@outschool/gql-frontend-generated";
import {
  faChevronLeft,
  faChevronRight,
  faClock,
  faCreditCard,
  faDiploma,
  faExclamationTriangle,
  fasPlayCircle,
  fasStar,
} from "@outschool/icons";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import {
  activityPath,
  createPathWithActivity,
  leaderPath,
} from "@outschool/routes";
import { useProductClicked } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  PublicReviewsOfLeaderModal,
  StarRatings,
  TeacherVideoModal,
  numberFormatter,
  useCredentialString,
  usePriceDetailsString,
  useTutorMatchContext,
} from "@outschool/ui-components-website";
import {
  Link,
  TrackingProperties,
} from "@outschool/ui-legacy-component-library";
import { Screen, useNavigation } from "@outschool/ui-utils";
import head from "lodash/head";
import React from "react";

import { useMembershipRequirement } from "../../subscriptions/hooks/useMembershipRequirement";
import CtaJoinMembershipWrapper from "./CtaJoinMembershipWrapper";

const RIBBON_OPEN_SECTIONS_THRESHOLD = 10;

type TutorCardV2Props = {
  listing: ListingCard_ListingFragment | LightListingCard_ListingFragment;
  trackingProperties: TrackingProperties;
};

/**
 * TutorCardV2
 *
 * Expected Tracking Behavior:
 * - Impression events (upon card entering viewport)
 *   - Activity Impression (sends 'Activity Impression' event to Segment)
 * - Click events
 *   - all interactive elements (buttons etc) fire their own click event
 *   - all interactive elements fire an 'Activity Impression Click' event (via Link component onClick handler)
 *   - the Link component onClick handler handles generic click tracking for the card, but only
 *     navigates when the card itself is clicked (rather than a button etc)
 *   - all interactive elements fire a 'Product Clicked' event via trackSearchImpressionClick()
 * (Note: this tracking spec could be revised in the future to be less redundant)
 */

const TutorCardV2 = ({ listing, trackingProperties }: TutorCardV2Props) => {
  const { translate } = useMachineTranslation();
  const { t } = useTranslation("client\\components\\listings\\ListingCard");
  const { isLoggedIn } = useSession();
  const isMobile = Screen.useIsMobile();
  const [showVideoModal, setShowVideoModal] = React.useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = React.useState(false);
  const trackProductClicked = useProductClicked();

  const { setTutorMatchEnrollmentData } = useTutorMatchContext();
  const { translateShortDegreeType, translateDegreeSubject } =
    useCredentialString();

  const { leader, activity } = listing;
  const navigate = useNavigation();

  const activityReviews = activity.recentReviews
    ? activity.recentReviews.filter(review => review.activity_star_rating === 5)
    : [];

  const bookThisTutorOnClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (activity.hasTeacherSchedule && isLoggedIn) {
      setTutorMatchEnrollmentData?.({
        activity,
      });
    } else {
      navigate(`${activityPath(activity)}#availability`, {
        hardNav: true,
        newTab: true,
      });
    }
  };

  const [currentActivityReviewIndex, setCurrentActivityReviewIndex] =
    React.useState(0);

  const { showCredits } = useMembershipRequirement();

  const currentActivityReview = activityReviews[currentActivityReviewIndex];

  // TODO: This may or may not be present depending on the type
  const openSectionCount = listing.hasOwnProperty("openSectionCount")
    ? (listing as ListingCard_ListingFragment).openSectionCount
    : undefined;

  const showCardRibbon =
    !!openSectionCount &&
    openSectionCount > 0 &&
    openSectionCount < RIBBON_OPEN_SECTIONS_THRESHOLD;

  const credential = leader.credentials
    ? head([...leader.credentials].sort(credentialDisplaySort))
    : undefined;

  const { perMeetingPriceString } = usePriceDetailsString();

  const Buttons = () => {
    return (
      <Box
        flex
        sx={(theme: Theme) => ({
          gap: "0.25em",
          [theme.breakpoints.down("md")]: {
            paddingLeft: "0.5em",
            paddingBottom: "0.5em",
          },
        })}
      >
        <Button
          variant={"outlined"}
          size="small"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            navigate(leaderPath(leader.uid, leader.leader_link), {
              hardNav: true,
              newTab: true,
            });
          }}
        >
          {t("View Profile")}
        </Button>
        {!activity.isSelfPaced && (
          <CtaJoinMembershipWrapper
            activity={activity}
            loggedInCta={t("Schedule")}
            controlComponent={
              <TrackedButton
                variant="contained"
                size="small"
                onClick={bookThisTutorOnClick}
                trackingName={"tutor_calendar_enrollment_button"}
                trackingUniqueId={activity.uid}
                trackingSharedProperties={{
                  activity_uid: activity.uid,
                }}
              >
                {t("Schedule")}
              </TrackedButton>
            }
            size="small"
            isMobile={isMobile}
            loggedInJoinCtaTrackingName={"tutor_clp_cta_join_to_view_plans"}
            loggedOutDesktopJoinCtaTrackingName={
              "tutor_clp_cta_auth_button_join_to_view_plans_desktop"
            }
            loggedOutMobileJoinCtaTrackingName={
              "tutor_clp_cta_auth_join_to_view_plans_mobile"
            }
          />
        )}
      </Box>
    );
  };

  const credits = OSSubscriptions.priceCentsToCredits(
    centsPerLearnerPerMeeting(activity),
  );

  return (
    <Box sx={{ position: "relative" }}>
      <Link
        to={createPathWithActivity(activity, null)}
        {...trackingProperties}
        useLinkTracking
        trackViews={true}
        onClick={() => {
          trackProductClicked(listing);
        }}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            margin: "0.5em",
            backgroundColor: theme.palette.common.white,
            borderRadius: "1em",
            cursor: "pointer",
            boxShadow: "0 4px 8px rgba(0, 0, 0, .06)",
            "&:hover": {
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.25)",
            },
            flexDirection: "column",
            [theme.breakpoints.down("md")]: {
              margin: "0",
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
              borderRadius: "0",
              boxShadow: "none",
            },
          })}
        >
          <Box
            flex
            sx={(theme: Theme) => ({
              flexDirection: "row",
              gap: "1em",
              padding: "1em",
              [theme.breakpoints.down("lg")]: {
                maxHeight: "300px",
              },
            })}
          >
            <Box
              sx={{
                flex: "1",
              }}
            >
              <Box
                sx={{
                  backgroundImage: `url("${leader.photo || ""}")`,
                  paddingBottom: "100%",
                  backgroundSize: "cover",
                  width: "100%",
                  borderRadius: "1em",
                  position: "relative",
                }}
              >
                {!!leader.video?.file.url && (
                  <Icon
                    icon={fasPlayCircle}
                    sx={(theme: Theme) => ({
                      position: "absolute",
                      color: theme.palette.common.white,
                      width: "48px",
                      height: "48px",
                      top: "50%",
                      left: "50%",
                      [theme.breakpoints.down("md")]: {
                        top: "80%",
                        left: "80%",
                      },
                      transform: "translate(-50%, -50%)",
                      filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))",
                      cursor: "pointer",
                      [theme.breakpoints.down("md")]: {
                        width: "28px",
                        height: "28px",
                        top: "85%",
                        left: "85%",
                      },
                    })}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowVideoModal(true);
                    }}
                  />
                )}
              </Box>
            </Box>
            {/* Educator details */}
            <Box
              flex
              sx={{
                flex: "1",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box flex sx={{ flexDirection: "column", gap: "0.5em" }}>
                <Typography
                  variant={"h6"}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: "1",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {leader.name}
                </Typography>
                <Box>
                  <Chip size="small" label={activity.subject} />
                  <Chip
                    sx={{ marginLeft: "0.25em" }}
                    size="small"
                    label={t("Age {{ageMin}} - {{ageMax}}", {
                      ageMin: activity.age_min,
                      ageMax: activity.age_max,
                    })}
                  />
                </Box>
                {leader.reviewCount > 0 && (
                  <DetailItem
                    icon={
                      <Icon
                        icon={fasStar}
                        sx={(theme: Theme) => ({
                          width: "24px",
                          color: theme.palette.warning.main,
                        })}
                      />
                    }
                  >
                    <Typography variant={"body2"} emphasized>
                      {leader.averageActivityStarRating?.toFixed(1)}
                    </Typography>
                    <Typography sx={{ color: "neutral.600" }} variant={"body2"}>
                      {t(" {{ reviewCount }} reviews", {
                        reviewCount: numberFormatter(leader.reviewCount),
                      })}
                    </Typography>
                  </DetailItem>
                )}
                {credential && credential.degreeType && (
                  <DetailItem
                    key={credential.uid}
                    icon={<Icon icon={faDiploma} sx={{ width: "24px" }} />}
                  >
                    <Typography component="span" variant={"body2"} emphasized>
                      {translateShortDegreeType(credential.degreeType)}
                    </Typography>
                    {credential.degreeSubject &&
                      credential.degreeSubject !== DegreeSubject.Other && (
                        <Typography component="span" variant={"body2"}>
                          {t(" in {{degreeSubject}}", {
                            degreeSubject: translateDegreeSubject(
                              credential.degreeSubject,
                            ),
                          })}
                        </Typography>
                      )}
                  </DetailItem>
                )}
                <DetailItem
                  icon={<Icon icon={faClock} sx={{ width: "24px" }} />}
                >
                  <Typography component="span" variant={"body2"} emphasized>
                    {t("{{ duration_minutes }} minutes ", {
                      duration_minutes: activity.duration_minutes,
                    })}
                  </Typography>
                  <Typography component="span" variant={"body2"}>
                    {t("per session")}
                  </Typography>
                </DetailItem>
                <DetailItem
                  icon={
                    <Icon
                      icon={faCreditCard}
                      sx={{
                        width: "24px",
                      }}
                    />
                  }
                >
                  {!!activity.duration_weeks && !!activity.weekly_meetings && (
                    <>
                      <Typography component="span" variant={"body2"} emphasized>
                        {t("{{sessions}} ", {
                          sessions:
                            activity.duration_weeks * activity.weekly_meetings,
                        })}
                      </Typography>
                      <Typography component="span" variant={"body2"}>
                        {t("sessions, ")}
                      </Typography>
                    </>
                  )}
                  {showCredits ? (
                    <Typography component="span" variant={"body2"} emphasized>
                      {t("{{ credits }} credit", {
                        credits: credits,
                        count: credits,
                        defaultValue_plural: "{{ credits }} credits",
                      })}
                    </Typography>
                  ) : (
                    <Typography component="span" variant={"body2"} emphasized>
                      {t("{{ price }}", {
                        price: perMeetingPriceString(activity),
                      })}
                    </Typography>
                  )}
                  <Typography component="span" variant={"body2"}>
                    {t(" per session")}
                  </Typography>
                </DetailItem>
              </Box>
              {!isMobile && <Buttons />}
            </Box>
          </Box>
          {isMobile && <Buttons />}
          {/* Reviews */}
          {!isMobile &&
            (currentActivityReview ? (
              <Box
                flex
                sx={(theme: Theme) => ({
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderTop: `1px solid ${theme.palette.grey[300]}`,
                  padding: "1em",
                  height: "140px",
                })}
              >
                <Typography
                  as={Box}
                  variant={"body2"}
                  sx={{
                    color: "neutral.600",
                    display: "-webkit-box",
                    WebkitLineClamp: "3",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {currentActivityReview.details?.publicReview &&
                    translate(currentActivityReview.details.publicReview)}
                </Typography>

                <Box
                  flex
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginTop: "0.5em",
                    height: "36px",
                  }}
                >
                  <Box
                    flex
                    sx={(theme: Theme) => ({
                      alignItems: "center",
                      gap: "0.5em",
                      [theme.breakpoints.down("md")]: {
                        flexDirection: "column",
                      },
                    })}
                  >
                    <Typography variant="caption" emphasized>
                      {currentActivityReview.parent?.publicName}
                    </Typography>
                    {currentActivityReview.activity_star_rating && (
                      <StarRatings
                        rating={currentActivityReview.activity_star_rating!}
                      />
                    )}
                  </Box>

                  <Box
                    flex
                    sx={{
                      flexDirection: "row",
                    }}
                  >
                    <TrackedButton
                      variant="link"
                      color="primary"
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        setIsReviewModalOpen(true);
                      }}
                      trackingName={"tutor_see_all_reviews_button"}
                      trackingUniqueId={activity.uid}
                    >
                      {t("See all reviews")}
                    </TrackedButton>
                    {activityReviews.length > 1 && (
                      <>
                        <IconButton
                          icon={faChevronLeft}
                          variant={"text"}
                          sx={{ padding: "0.5em" }}
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            const newIndex =
                              (currentActivityReviewIndex - 1) %
                              activityReviews.length;
                            setCurrentActivityReviewIndex(
                              newIndex < 0
                                ? activityReviews.length - 1
                                : newIndex,
                            );
                          }}
                        />
                        <IconButton
                          icon={faChevronRight}
                          variant={"text"}
                          sx={{ padding: "0.5em" }}
                          onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            setCurrentActivityReviewIndex(
                              (currentActivityReviewIndex + 1) %
                                activityReviews.length,
                            );
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box
                flex
                sx={(theme: Theme) => ({
                  flexDirection: "column",
                  justifyContent: "space-between",
                  borderTop: `1px solid ${theme.palette.grey[300]}`,
                  padding: "1.5em",
                  height: "140px",
                })}
              >
                <Typography
                  as={Box}
                  variant={"body2"}
                  sx={{
                    color: "neutral.600",
                    display: "-webkit-box",
                    WebkitLineClamp: "3",
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {t("Be the first to review this 1-on-1 session!")}
                </Typography>
                <Box flex sx={{ justifyContent: "right" }}>
                  <Button
                    variant="link"
                    color="primary"
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setIsReviewModalOpen(true);
                    }}
                  >
                    {t("See all educator reviews")}
                  </Button>
                </Box>
              </Box>
            ))}
        </Box>
      </Link>
      {showCardRibbon && (
        <CardRibbon
          icon={<Icon icon={faExclamationTriangle} />}
          text={t("Only {{openSectionCount}} Time Slot Left", {
            openSectionCount,
            count: openSectionCount,
            defaultValue_plural: "Only {{openSectionCount}} Time Slots Left",
          })}
        />
      )}

      <TeacherVideoModal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
        }}
        teacher={leader}
      />
      {leader.averageActivityStarRating && (
        <PublicReviewsOfLeaderModal
          leaderUid={leader.uid}
          isReviewModalOpen={isReviewModalOpen}
          setIsReviewModalOpen={setIsReviewModalOpen}
          averageActivityStarRating={leader.averageActivityStarRating}
          reviewCount={leader.reviewCount}
        />
      )}
    </Box>
  );
};

const DetailItem = ({
  icon,
  children,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
}) => {
  return (
    <Box flex sx={{ flexDirection: "row", alignItems: "center" }}>
      <Box sx={{ marginRight: "0.5em" }}>{icon}</Box>
      <Box>{children}</Box>
    </Box>
  );
};

const CardRibbon = ({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) => {
  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        position: "absolute",
        flexDirection: "row",
        alignItems: "center",
        gap: "0.5em",
        top: "1.5em",
        lineHeight: "2em",
        paddingX: "0.5em",
        width: "fit-content",
        borderRadius: "0.25em",
        color: theme.palette.warning.dark,
        backgroundColor: theme.palette.warning.light,
      })}
    >
      {icon}
      {text}
    </Box>
  );
};

export default TutorCardV2;
